/**
 * G.711 µ-law — dasselbe Audioformat, das auch am Telefon läuft.
 *
 * Das Probegespräch spricht bewusst exakt das Protokoll und Format einer
 * Twilio-Media-Stream-Verbindung (µ-law, 8 kHz, mono, 20-ms-Rahmen à 160
 * Byte). Nur deshalb kann der Voice-Server das Gespräch durch denselben
 * Anrufweg schicken wie einen echten Anruf — ohne zweite, "ähnliche"
 * Gesprächsstrecke, die dann anders klingt als das Produkt.
 */

const MULAW_BIAS = 0x84;
const MULAW_CLIP = 32635;

/** Ein PCM16-Sample (−32768…32767) in ein µ-law-Byte. */
export function linearToMulaw(sample: number): number {
  let wert = sample;
  let vorzeichen = (wert >> 8) & 0x80;
  if (vorzeichen !== 0) wert = -wert;
  if (wert > MULAW_CLIP) wert = MULAW_CLIP;
  wert += MULAW_BIAS;

  let exponent = 7;
  for (let maske = 0x4000; (wert & maske) === 0 && exponent > 0; exponent--, maske >>= 1) {
    /* Exponent ist die Position des höchsten gesetzten Bits. */
  }
  const mantisse = (wert >> (exponent + 3)) & 0x0f;
  return ~(vorzeichen | (exponent << 4) | mantisse) & 0xff;
}

/** Ein µ-law-Byte zurück in ein PCM16-Sample. */
export function mulawToLinear(byte: number): number {
  const wert = ~byte & 0xff;
  const vorzeichen = wert & 0x80;
  const exponent = (wert >> 4) & 0x07;
  const mantisse = wert & 0x0f;
  let sample = ((mantisse << 3) + MULAW_BIAS) << exponent;
  sample -= MULAW_BIAS;
  return vorzeichen !== 0 ? -sample : sample;
}

/**
 * Die 256 µ-law-Bytes einmal vorausgerechnet als Float32 (−1…1).
 * Wiedergabe läuft damit ohne Rechnerei pro Sample.
 */
export const MULAW_DECODE_TABLE: Float32Array = (() => {
  const tabelle = new Float32Array(256);
  for (let i = 0; i < 256; i++) tabelle[i] = mulawToLinear(i) / 32768;
  return tabelle;
})();

/** Float32-Block (−1…1) → µ-law-Bytes. */
export function floatToMulaw(block: Float32Array): Uint8Array {
  const out = new Uint8Array(block.length);
  for (let i = 0; i < block.length; i++) {
    const begrenzt = Math.max(-1, Math.min(1, block[i]));
    out[i] = linearToMulaw(Math.round(begrenzt * 32767));
  }
  return out;
}

/** µ-law-Bytes → Float32-Block (−1…1). */
export function mulawToFloat(bytes: Uint8Array): Float32Array {
  const out = new Float32Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) out[i] = MULAW_DECODE_TABLE[bytes[i]];
  return out;
}

/** Bytes → base64, wie Twilio es im `media`-Ereignis erwartet. */
export function bytesToBase64(bytes: Uint8Array): string {
  let binaer = '';
  for (let i = 0; i < bytes.length; i++) binaer += String.fromCharCode(bytes[i]);
  return btoa(binaer);
}

/** base64 → Bytes. */
export function base64ToBytes(b64: string): Uint8Array {
  const binaer = atob(b64);
  const out = new Uint8Array(binaer.length);
  for (let i = 0; i < binaer.length; i++) out[i] = binaer.charCodeAt(i);
  return out;
}
